import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const PAGE_SIZE = 25;

// Categories as an indented, depth-first list (sections, their chapters, their sub-categories)
function indented(itemTypes) {
  const byParent = {};
  itemTypes.forEach((t) => {
    const key = t.parent?.id ?? 'root';
    (byParent[key] = byParent[key] || []).push(t);
  });
  const out = [];
  const visit = (key, depth) => {
    (byParent[key] || []).slice().sort((a, b) => a.name.localeCompare(b.name)).forEach((t) => {
      out.push({ id: t.id, label: `${'   '.repeat(depth)}${depth ? '└ ' : ''}${t.name}` });
      visit(t.id, depth + 1);
    });
  };
  visit('root', 0);
  return out;
}

/**
 * Org Admin > Materials and Products: the Global Material Master to browse and search, a page at a time, instead of one long list in a dialog. Each row's
 * "+ Add" opens the usual form for the organization's own details (SKU, unit, reorder levels, opening stock) with that item chosen.
 * `reloadKey` changes when something was added, so the list shows it as added (or drops it).
 */
function MaterialCatalogBrowser({ itemTypes, reloadKey, startOpen, onAdd, onMakeVariant, onCreateOwn, forProducts = false }) {
  const noun = forProducts ? 'product' : 'material';
  const [open, setOpen] = useState(startOpen);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [hideAdded, setHideAdded] = useState(true);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const categories = useMemo(() => indented(itemTypes || []), [itemTypes]);

  // The words typed settle for a moment before they are searched
  useEffect(() => {
    const t = setTimeout(() => { setQuery(text.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [text]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get('/api/organization-materials/catalog', {
      params: { q: query || undefined, categoryId: categoryId || undefined, hideAdded, forProducts: forProducts || undefined, page, size: PAGE_SIZE }
    })
      .then((r) => { if (!cancelled) setResult(r.data); })
      .catch((e) => {
        if (!cancelled) toast.error(typeof e.response?.data === 'string' ? e.response.data : 'Could not load the Global Material Master');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, query, categoryId, hideAdded, page, reloadKey, forProducts]);

  const from = result && result.total > 0 ? result.page * result.size + 1 : 0;
  const to = result ? Math.min(result.total, (result.page + 1) * result.size) : 0;

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h6 className="mb-0">Add from the Global Material Master</h6>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={onCreateOwn}>Can&apos;t find it? Create my own {noun}</Button>
          <Button size="sm" variant="outline-primary" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</Button>
        </div>
      </Card.Header>
      {open && (
        <Card.Body>
          <p className="text-muted small">
            The Global Material Master is the list of materials shared by every business on iBigDo, each with its category and HSN code.
            Search it by name, HSN or category, then press <strong>+ Add</strong> to bring one into your {noun}s with your own details{forProducts ? '.' : <>, or <strong>Make a variant…</strong> for your own more specific one (a size or brand) started from it.</>}
          </p>
          <Row className="g-2 mb-3 align-items-end">
            <Col md={5}>
              <Form.Label className="small mb-1">Search</Form.Label>
              <Form.Control size="sm" value={text} placeholder="For example sheet, 7208, bolts" onChange={(e) => setText(e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="small mb-1">Category</Form.Label>
              <Form.Select size="sm" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(0); }}>
                <option value="">All categories</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Check type="checkbox" id="catalog-hide-added" label={forProducts ? 'Hide ones I already sell' : 'Hide ones I already have'} checked={hideAdded}
                onChange={(e) => { setHideAdded(e.target.checked); setPage(0); }} />
            </Col>
          </Row>

          {loading && !result ? (
            <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              <Table size="sm" bordered hover responsive className="align-middle mb-2">
                <thead>
                  <tr><th>Item</th><th>Category</th><th>HSN</th><th>About it</th><th style={{ width: 90 }} /></tr>
                </thead>
                <tbody>
                  {(result?.items || []).map((i) => (
                    <tr key={i.id}>
                      <td>
                        <strong>{i.name}</strong>
                        {i.own && <Badge bg="secondary" className="ms-2">yours</Badge>}
                        {i.parentItemName && <div className="small text-muted">filed under {i.parentItemName}</div>}
                        {i.variants && i.variants.length > 0 && (
                          <div className="small text-muted">your variants: {i.variants.join(', ')}</div>
                        )}
                      </td>
                      <td className="small" title={i.categoryPath || ''}>{i.category || '-'}</td>
                      <td className="small">{i.hsCode || '-'}</td>
                      <td className="small text-muted">{i.description || ''}</td>
                      <td className="text-end text-nowrap">
                        {i.added
                          ? <Badge bg="success">Added</Badge>
                          : <Button size="sm" variant="outline-primary" onClick={() => onAdd(i)} title={`Use it as it is, with its name and specification`}>+ Add</Button>}
                        {onMakeVariant && !forProducts && !i.parentItemId && (
                          <Button size="sm" variant="outline-secondary" className="ms-1" onClick={() => onMakeVariant(i)}
                            title="Your own, more specific material (a size or brand), started from this one and filed under it">Make a variant…</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {result && result.items.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        {query || categoryId
                          ? 'Nothing matches. Try fewer words or another category, or create your own ' + noun + '.'
                          : 'Nothing to add: everything in the Global Material Master is already in your ' + noun + 's.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {result && result.total > 0 && (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span className="text-muted small">Showing {from} to {to} of {result.total}{loading && ' (loading)'}</span>
                  <div className="d-flex align-items-center gap-2">
                    <Button size="sm" variant="outline-secondary" disabled={result.page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
                    <span className="small">Page {result.page + 1} of {Math.max(1, result.pages)}</span>
                    <Button size="sm" variant="outline-secondary" disabled={result.page + 1 >= result.pages || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      )}
    </Card>
  );
}

export default MaterialCatalogBrowser;
